import { useEffect, useState } from 'react'
import { Link, router } from '@inertiajs/react'
import { ArrowDown, ArrowUp, ChevronsUpDown, LayoutGrid, List, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useIsMobile } from '@/hooks/use-mobile'
import { TaskFilters } from './_components/TaskFilters'
import { TaskRow as TaskRowComponent } from './_components/TaskRow'
import { TaskDetailSheet } from './_components/TaskDetailSheet'
import { CreateTaskSheet } from './_components/CreateTaskSheet'
import { TasksKanbanBoard } from './_components/TasksKanbanBoard'
import { GroupedTaskTable } from './_components/GroupedTaskTable'
import { StackedTeamBoards } from './_components/StackedTeamBoards'
import type {
  TaskRow,
  PagyMeta,
  TaskIndexFilters,
  TaskDisplay,
  TaskSortKey,
  TeamGroup,
} from '@/types/work'

interface Props {
  tasks: TaskRow[]
  pagy: PagyMeta
  filters: TaskIndexFilters
  // US16: present only when grouping is active (group_by=team). Carries
  // the hierarchy-ordered, non-empty team groups with true per-team counts.
  team_groups?: TeamGroup[]
}

export default function TasksIndex({ tasks, pagy, filters, team_groups }: Props) {
  // Sheet state is component-local. Clicking a row sets the id; the
  // sheet opens, fetches detail JSON, and renders the existing Show
  // component in embedded mode. Closing the sheet (overlay/ESC/close)
  // resets to null and the list view stays put with its scroll/filters.
  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null)
  const [createOpen, setCreateOpen] = useState(false)

  // Sprint 5b U3 + U9: the toggle reflects the server-echoed display, but
  // mobile clients (≤768px) force-render the list regardless of `?display=`
  // so the user's bookmarked Kanban URL is preserved across viewport
  // changes — they get the list now, the board next time they open it on
  // a desktop. (See plan §Mode-switching matrix.)
  const isMobile = useIsMobile()
  const needsMobileListPayload = isMobile && filters.display === 'kanban'
  const effectiveDisplay: TaskDisplay = isMobile ? 'list' : filters.display

  // PR #95 follow-up: rendering the list UI on top of a Kanban payload
  // silently hides tasks (Kanban caps records at 500 open + 3×50 closed
  // and synthesises `pages: 1`). Re-fetch with `?display=list` whenever
  // the viewport is mobile but the server gave us the Kanban dataset, so
  // the list renders against the proper paginated payload. `replace: true`
  // keeps the back button from bouncing between the kanban URL and the
  // mobile list URL. The condition self-clears once the new props arrive
  // (filters.display flips to 'list').
  useEffect(() => {
    if (!needsMobileListPayload) return
    const url = new URL(window.location.href)
    url.searchParams.set('display', 'list')
    router.get(
      url.pathname + url.search,
      {},
      { preserveState: true, preserveScroll: true, replace: true },
    )
  }, [needsMobileListPayload])

  function handleDisplayChange(next: string) {
    if (isMobile) return
    if (next !== 'kanban' && next !== 'list') return
    if (next === filters.display) return
    // Build the new URL by mutating only `display` so all other filter
    // params (status[], priority[], etc.) survive. router.get with an
    // object would re-serialise array params; using the URL string keeps
    // the existing TaskFilters URL conventions intact.
    const url = new URL(window.location.href)
    url.searchParams.set('display', next)
    router.get(url.pathname + url.search, {}, { preserveState: true, preserveScroll: true })
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Tâches</h1>
          <p className="text-muted-foreground">
            Gestion et suivi des tâches assignées dans votre périmètre.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Nouvelle tâche
        </Button>
      </div>

      <TaskFilters filters={filters} />

      <div className="space-y-2">
        <Tabs value={effectiveDisplay} onValueChange={handleDisplayChange}>
          <TabsList>
            <TabsTrigger value="kanban" disabled={isMobile}>
              <LayoutGrid className="mr-1.5 h-4 w-4" />
              Tableau
            </TabsTrigger>
            <TabsTrigger value="list">
              <List className="mr-1.5 h-4 w-4" />
              Liste
            </TabsTrigger>
          </TabsList>
        </Tabs>
        {isMobile ? (
          // Disabled <TabsTrigger> drops out of tab order and has no hover,
          // so a tooltip is unreachable on touch (DR-T2-O). Render visible
          // helper text instead.
          <p className="text-xs text-muted-foreground">
            Mode tableau disponible sur grand écran (≥768px).
          </p>
        ) : null}
      </div>

      {effectiveDisplay === 'kanban' ? (
        <KanbanLayout
          tasks={tasks}
          filters={filters}
          teamGroups={team_groups ?? []}
          onTaskClick={setSelectedTaskId}
          onCreate={() => setCreateOpen(true)}
        />
      ) : filters.group_by === 'team' ? (
        // US16 U6: grouped list — collapsible per-team sections instead of
        // the flat paginated table. `team_groups` is present whenever
        // grouping is active (server only emits it then).
        <GroupedTaskTable
          tasks={tasks}
          teamGroups={team_groups ?? []}
          onTaskClick={setSelectedTaskId}
        />
      ) : (
        <ListLayout tasks={tasks} pagy={pagy} filters={filters} onTaskClick={setSelectedTaskId} />
      )}

      <TaskDetailSheet
        taskId={selectedTaskId}
        onOpenChange={(open) => { if (!open) setSelectedTaskId(null) }}
        onSelectTask={setSelectedTaskId}
      />

      <CreateTaskSheet
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(id) => {
          // Close the create sheet and immediately open the new task in
          // the detail sheet so the user can keep editing / commenting.
          setCreateOpen(false)
          setSelectedTaskId(id)
        }}
      />
    </div>
  )
}

// U10 — wraps TasksKanbanBoard with empty-state CTAs for the two
// distinct empty cases:
//   * Empty perimeter: zero tasks AND no filters active → invite the
//     user to create their first task.
//   * Filter-empty: zero tasks WITH active filters → offer a
//     clear-filters affordance (preserves display=kanban).
function KanbanLayout({
  tasks,
  filters,
  teamGroups,
  onTaskClick,
  onCreate,
}: {
  tasks: TaskRow[]
  filters: TaskIndexFilters
  teamGroups: TeamGroup[]
  onTaskClick: (id: number) => void
  onCreate: () => void
}) {
  if (tasks.length === 0) {
    if (hasActiveFilters(filters)) {
      return (
        <EmptyState
          title="Aucune tâche ne correspond aux filtres actifs."
          actionLabel="Effacer les filtres"
          onAction={() =>
            // Drop every filter param; preserve display so the user stays
            // in Kanban mode after clearing.
            router.get('/work/tasks', { display: 'kanban' }, { preserveScroll: true })
          }
        />
      )
    }
    return (
      <EmptyState
        title="Aucune tâche dans votre périmètre."
        actionLabel="Nouvelle tâche"
        onAction={onCreate}
      />
    )
  }
  // US16 U7: grouped Kanban — stacked per-team boards. The zero-task
  // empty states above still apply; the grouped path only renders once
  // tasks exist (and therefore team_groups is non-empty).
  if (filters.group_by === 'team') {
    return (
      <StackedTeamBoards
        tasks={tasks}
        teamGroups={teamGroups}
        onTaskClick={onTaskClick}
      />
    )
  }
  return <TasksKanbanBoard tasks={tasks} onTaskClick={onTaskClick} />
}

function EmptyState({
  title,
  actionLabel,
  onAction,
}: {
  title: string
  actionLabel: string
  onAction: () => void
}) {
  return (
    <div className="rounded-md border border-dashed py-16 text-center">
      <p className="text-sm font-medium text-foreground">{title}</p>
      <Button variant="outline" size="sm" className="mt-4" onClick={onAction}>
        {actionLabel}
      </Button>
    </div>
  )
}

function hasActiveFilters(f: TaskIndexFilters): boolean {
  // PR #100 review (codex P2): only `view === 'mine'` is restrictive
  // server-side. `filter_view` in TasksController#index treats `team`
  // (and nil) as the same no-op perimeter scope. Counting `team` as
  // active would route an empty Team tab to the filter-empty CTA
  // instead of the empty-perimeter CTA.
  return (
    f.view === 'mine' ||
    f.status.length > 0 ||
    f.priority.length > 0 ||
    f.type.length > 0 ||
    f.assignee_id.length > 0 ||
    f.team_id.length > 0 ||
    !!f.subject_type ||
    !!f.due ||
    !!f.q
  )
}

function ListLayout({
  tasks,
  pagy,
  filters,
  onTaskClick,
}: {
  tasks: TaskRow[]
  pagy: PagyMeta
  filters: TaskIndexFilters
  onTaskClick: (id: number) => void
}) {
  // US14: sorting navigates via the live query string (mirrors
  // ServerDataTable.navigateWith) so every other param — filters, display,
  // per_page — survives untouched. `page` is dropped because sorting
  // returns to page 1. The toggle reads the server-echoed filters.sort /
  // filters.dir, so re-activating the applied column reverses it.
  function handleSort(key: TaskSortKey) {
    const nextDir = filters.sort === key && filters.dir === 'asc' ? 'desc' : 'asc'
    const params = new URLSearchParams(window.location.search)
    params.set('sort', key)
    params.set('dir', nextDir)
    params.delete('page')
    router.get(`${window.location.pathname}?${params.toString()}`, {}, { preserveState: true, preserveScroll: true })
  }

  const sortableHead = (label: string, key: TaskSortKey, className?: string) => (
    <SortableHead
      label={label}
      sortKey={key}
      activeSort={filters.sort}
      activeDir={filters.dir}
      onSort={handleSort}
      className={className}
    />
  )

  return (
    <>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              {sortableHead('Titre', 'title')}
              {sortableHead('Statut', 'status', 'w-[160px]')}
              {sortableHead('Priorité', 'priority', 'w-[120px]')}
              {sortableHead('Type', 'type', 'w-[120px]')}
              {sortableHead('Échéance', 'due_at', 'w-[120px]')}
              {sortableHead('Assigné à', 'assignee', 'w-[160px]')}
              {sortableHead('Équipe', 'team', 'w-[160px]')}
              {sortableHead('Créé par', 'created_by', 'w-[160px]')}
              {/* Lié à stays unsortable: polymorphic link, no order (R2). */}
              <TableHead>Lié à</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tasks.length === 0 ? (
              <TableRow>
                <td
                  colSpan={9}
                  className="h-24 text-center text-sm text-muted-foreground"
                >
                  Aucune tâche.
                </td>
              </TableRow>
            ) : (
              // Inline status edit is enabled optimistically; the server
              // is authoritative — a 403 rolls the cell back. (Per-row
              // policy_caps will gate this client-side once the index
              // serializer attaches them — TODO follow-up.)
              tasks.map((t) => (
                <TaskRowComponent
                  key={t.id}
                  task={t}
                  canEditStatus={true}
                  onTaskClick={onTaskClick}
                />
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Pagination pagy={pagy} />
    </>
  )
}

// US14: `aria-sort` rides on the <th>, not the <button> — screen readers
// announce sort state from the column header per the ARIA grid pattern.
function SortableHead({
  label,
  sortKey,
  activeSort,
  activeDir,
  onSort,
  className,
}: {
  label: string
  sortKey: TaskSortKey
  activeSort: TaskIndexFilters['sort']
  activeDir: TaskIndexFilters['dir']
  onSort: (key: TaskSortKey) => void
  className?: string
}) {
  const isSorted = activeSort === sortKey
  const ariaSort: 'ascending' | 'descending' | 'none' = isSorted
    ? activeDir === 'asc'
      ? 'ascending'
      : 'descending'
    : 'none'
  const Icon = isSorted ? (activeDir === 'asc' ? ArrowUp : ArrowDown) : ChevronsUpDown
  return (
    <TableHead aria-sort={ariaSort} className={className}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className="inline-flex items-center gap-1 select-none hover:text-foreground"
      >
        {label}
        <Icon className={isSorted ? 'h-3.5 w-3.5' : 'h-3.5 w-3.5 opacity-40'} />
      </button>
    </TableHead>
  )
}

function Pagination({ pagy }: { pagy: PagyMeta }) {
  if (pagy.pages <= 1) return null
  // Pagy is 1-indexed. Build the URL via the current location so we
  // preserve filter params on page navigation.
  const params = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null
  const linkFor = (page: number) => {
    if (!params) return `?page=${page}`
    params.set('page', String(page))
    return `?${params.toString()}`
  }

  // Codex P2 #89 (also applies here): `Button asChild disabled` wraps
  // an anchor and the disabled attribute is dropped. Render a real
  // <button disabled> at the boundaries so out-of-range page=0 /
  // page=last+1 navigations cannot fire.
  const prevDisabled = pagy.page <= 1
  const nextDisabled = pagy.page >= pagy.pages

  return (
    <div className="flex items-center justify-between text-sm">
      <p className="text-muted-foreground">
        {pagy.count} tâche{pagy.count > 1 ? 's' : ''} — page {pagy.page} sur {pagy.pages}
      </p>
      <div className="flex items-center gap-2">
        {prevDisabled ? (
          <Button variant="outline" size="sm" disabled>
            Précédent
          </Button>
        ) : (
          <Button asChild variant="outline" size="sm">
            <Link href={linkFor(pagy.page - 1)} preserveScroll preserveState>
              Précédent
            </Link>
          </Button>
        )}
        {nextDisabled ? (
          <Button variant="outline" size="sm" disabled>
            Suivant
          </Button>
        ) : (
          <Button asChild variant="outline" size="sm">
            <Link href={linkFor(pagy.page + 1)} preserveScroll preserveState>
              Suivant
            </Link>
          </Button>
        )}
      </div>
    </div>
  )
}
